import { format, parseISO } from "date-fns"
import { useEffect, useState } from "preact/hooks"
import { usePageTitle } from "@/utils"

import { BlogAuthorByline } from "./author_byline"
import { BlogLayout } from "./blog_layout"
import { useBlogBootstrap } from "./bootstrap"
import { fetch_blog_posts } from "./fetch"
import { display_dimensions_for_image_width } from "./image_dimensions"
import { url_for_sanity_image } from "./image_url"
import type { BlogPostListItem } from "./types"

export function BlogIndexPage() {
  usePageTitle("Blog")

  const bootstrap = useBlogBootstrap()
  /** Non-empty SSR list only — empty `[]` must still fetch (posts may exist after build or without prerender env). */
  const ssr_posts =
    bootstrap?.kind === "index" && bootstrap.posts.length > 0 ? bootstrap.posts : undefined

  const [posts, setPosts] = useState<BlogPostListItem[]>(() => ssr_posts ?? [])
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(() => ssr_posts === undefined)

  useEffect(() => {
    // The prerendered list is what existed at build time. Show it at once, then always refresh from
    // Sanity: a post that went live since the last deploy would otherwise stay off the index.
    const has_ssr_posts = bootstrap?.kind === "index" && bootstrap.posts.length > 0
    if (bootstrap?.kind === "index" && bootstrap.posts.length > 0) {
      setPosts(bootstrap.posts)
      setError(null)
      setLoading(false)
    }
    let cancelled = false
    if (!has_ssr_posts) {
      setLoading(true)
    }
    fetch_blog_posts()
      .then((p) => {
        if (!cancelled) {
          setPosts(p)
          setError(null)
        }
      })
      .catch((e) => {
        // A failed refresh leaves the prerendered list on screen.
        if (!cancelled && !has_ssr_posts) {
          setError(e instanceof Error ? e : new Error(String(e)))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [bootstrap])

  return (
    <BlogLayout>
      <div class="max-w-3xl">
        <p class="text-xs font-bold uppercase tracking-[0.2em] text-salmon">Insights</p>
        <h1 class="font-display mt-3 text-4xl font-bold tracking-tight text-indigo-950 md:text-5xl">MarginWiz blog</h1>
        <p class="mt-4 max-w-2xl text-lg leading-relaxed text-indigo-800">
          Product updates, trade promotion tips, and how teams recover more deductions—without the spreadsheet chaos.
        </p>
      </div>

      {error ? (
        <p class="mt-10 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
          Could not load posts. Check Sanity configuration.
        </p>
      ) : null}

      {!error && loading && posts.length === 0 ? <BlogIndexSkeleton /> : null}

      {!loading && !error && posts.length === 0 ? (
        <p class="mt-12 rounded-2xl border border-sky-200 bg-white/80 px-5 py-6 text-indigo-800 shadow-sm">
          No posts yet. Add content in Sanity Studio once{" "}
          <code class="rounded bg-sky-100 px-1.5 py-0.5 font-mono text-sm text-indigo-950">VITE_SANITY_*</code> env vars
          are set.
        </p>
      ) : null}

      <ul class="mt-12 space-y-8 md:space-y-10">
        {posts.map((post) => (
          <li key={post._id}>
            <BlogPostCard post={post} />
          </li>
        ))}
      </ul>
    </BlogLayout>
  )
}

function BlogIndexSkeleton() {
  return (
    <div class="mt-12 space-y-8" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          class="animate-pulse overflow-hidden rounded-3xl border border-sky-100 bg-white/90 shadow-sm">
          <div class="flex flex-col gap-4 p-6 sm:flex-row sm:items-stretch sm:gap-8 sm:p-8">
            <div class="h-40 rounded-2xl bg-sky-100 sm:h-auto sm:min-h-[220px] sm:w-1/2 sm:shrink-0" />
            <div class="min-w-0 flex-1 space-y-3 pt-1">
              <div class="h-3 w-24 rounded bg-sky-100" />
              <div class="h-7 w-4/5 max-w-md rounded bg-sky-100" />
              <div class="h-4 w-full rounded bg-sky-50" />
              <div class="h-4 w-5/6 rounded bg-sky-50" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

const CARD_IMG_WIDTH = 1200
const CARD_IMG_FALLBACK_HEIGHT = 630

interface BlogPostCardProps {
  post: BlogPostListItem
}

function BlogPostCard(props: BlogPostCardProps) {
  const { post } = props
  const dateLabel = safe_format_date(post.publishedAt)
  const img = url_for_sanity_image(post.mainImage, CARD_IMG_WIDTH)
  const card_dims =
    display_dimensions_for_image_width(post.mainImage, CARD_IMG_WIDTH) ??
    ({ width: CARD_IMG_WIDTH, height: CARD_IMG_FALLBACK_HEIGHT } as const)

  return (
    <article class="group overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-sm transition-all duration-200 hover:border-sky-200 hover:shadow-md">
      <div class="flex flex-col gap-0 sm:flex-row sm:items-stretch">
        {img ? (
          <a
            href={`/blog/${post.slug}`}
            class="relative flex shrink-0 items-center justify-center overflow-hidden bg-sky-50/90 sm:w-1/2 sm:min-w-0">
            <img
              src={img}
              alt={post.mainImage?.alt ?? ""}
              width={card_dims.width}
              height={card_dims.height}
              class="h-48 w-full object-contain object-center transition-transform duration-300 group-hover:scale-[1.01] sm:h-full sm:min-h-[220px] sm:w-full sm:p-2"
            />
            <div class="pointer-events-none absolute inset-0 bg-linear-to-tr from-indigo-900/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
          </a>
        ) : null}
        <div class="flex min-w-0 flex-1 flex-col justify-center p-6 sm:p-8">
          <time class="text-xs font-semibold uppercase tracking-wide text-indigo-600" dateTime={post.publishedAt}>
            {dateLabel}
          </time>
          <BlogAuthorByline authorSlug={post.author} className="mt-2" size="sm" />
          <h2 class="font-display mt-3 text-2xl font-bold tracking-tight text-indigo-950 md:text-3xl">
            <a href={`/blog/${post.slug}`} class="transition-colors hover:text-indigo-600">
              {post.title}
            </a>
          </h2>
          {post.excerpt ? (
            <p class="mt-3 line-clamp-3 text-base leading-relaxed text-indigo-900">{post.excerpt}</p>
          ) : null}
          <div class="mt-5">
            <a
              href={`/blog/${post.slug}`}
              class="inline-flex items-center rounded-full border border-indigo bg-white px-4 py-2 text-xs font-bold uppercase tracking-wide text-indigo transition-colors hover:bg-indigo hover:text-white">
              Read article
            </a>
          </div>
        </div>
      </div>
    </article>
  )
}

function safe_format_date(iso: string): string {
  try {
    return format(parseISO(iso), "MMMM d, yyyy")
  } catch {
    return iso
  }
}
